import React, { useEffect, useRef } from 'react';
import { EffectContainer, renderEffects } from './utils/renderEffects.ts';
import { Application } from 'pixi.js';
import { AssetType, Effect, Layer, SpriteWithId } from '../../types/typing.ts';
import { isSunlightEffect } from './utils/effectPreviews.ts';
import { isBackgroundRecordingApp } from './utils/backgroundRecordingState';

interface EffectsRendererProps {
  app: Application | null;
  effectsSpriteRef: React.MutableRefObject<SpriteWithId | null>;
  effect: Effect[] | null | undefined;
  width: number;
  height: number;
  zIndex: number;
  layers: Layer[] | undefined;
}

const EffectsRenderer: React.FC<EffectsRendererProps> = ({
  app,
  effectsSpriteRef,
  effect,
  width,
  height,
  zIndex,
  layers,
}) => {
  const containersRef = useRef<Map<string, EffectContainer>>(new Map());
  const previousAppRef = useRef<Application | null>(null);
  const previousSizeRef = useRef({ width, height });

  const destroyEffectContainer = (container: EffectContainer) => {
    container.cleanup?.();
    container.parent?.removeChild(container);
    container.destroy({ children: true });
  };

  const clearEffectContainers = () => {
    containersRef.current.forEach(destroyEffectContainer);
    containersRef.current.clear();
    effectsSpriteRef.current = null;
  };

  const updateLayerOrder = () => {
    const stage = app?.stage;
    if (!stage) return;
    const backgroundLayerIndex =
      layers?.reduce(
        (lastIndex, layer, index) =>
          layer.type === AssetType.BACKGROUND ? index : lastIndex,
        -1
      ) ?? -1;

    containersRef.current.forEach((container) => {
      const matchingLayer = layers?.find((layer) => layer.id === container.id);
      const matchingEffect = effect?.find((item) => item.id === container.id);

      if (isSunlightEffect(matchingEffect?.type)) {
        container.zIndex =
          backgroundLayerIndex >= 0 ? backgroundLayerIndex + 0.5 : 0.5;
        return;
      }

      container.zIndex = matchingLayer ? layers!.indexOf(matchingLayer) : zIndex;
    });

    stage.sortChildren();
  };

  useEffect(() => {
    if (!app?.stage) return;

    const appChanged = previousAppRef.current !== app;
    const sizeChanged =
      previousSizeRef.current.width !== width ||
      previousSizeRef.current.height !== height;

    if (appChanged || sizeChanged) {
      clearEffectContainers();
      previousAppRef.current = app;
      previousSizeRef.current = { width, height };
    }

    let isCancelled = false;
    const currentEffects = effect ?? [];
    const currentEffectIds = new Set(currentEffects.map((item) => item.id));

    containersRef.current.forEach((container, effectId) => {
      if (!currentEffectIds.has(effectId)) {
        destroyEffectContainer(container);
        containersRef.current.delete(effectId);
      }
    });

    const effectsToRender = currentEffects.filter(
      (item) => !containersRef.current.has(item.id)
    );

    if (effectsToRender.length === 0) {
      updateLayerOrder();
      if (containersRef.current.size === 0) effectsSpriteRef.current = null;
      return;
    }

    renderEffects(app, effectsToRender, width, height, zIndex, effectsSpriteRef)
      .then((containers) => {
        if (isCancelled) {
          containers.forEach(destroyEffectContainer);
          return;
        }

        containers.forEach((container) => {
          if (!container.id || !currentEffectIds.has(container.id)) {
            destroyEffectContainer(container);
            return;
          }

          containersRef.current.set(container.id, container);
        });

        const allContainers = Array.from(containersRef.current.values());
        const lastContainer =
          containers[containers.length - 1] ??
          allContainers[allContainers.length - 1] ??
          null;

        effectsSpriteRef.current = lastContainer as unknown as SpriteWithId | null;
        updateLayerOrder();
      })
      .catch((error) => {
        console.error('Error rendering effects:', error);
      });

    return () => {
      isCancelled = true;
    };
  }, [app, effect, effectsSpriteRef, height, layers, width, zIndex]);

  useEffect(() => {
    return () => {
      const currentApp = previousAppRef.current;
      if (isBackgroundRecordingApp(currentApp)) {
        return;
      }

      clearEffectContainers();
    };
  }, []);

  return null;
};

export default EffectsRenderer;
